import { TRIMMED_INPUT_REGEX } from 'constants/constants';
import {
  EPopupSubType,
  ERewardCalendarFormState,
  ERewardCalendarMissedReset,
  ERewardCalendarPopUpTrigger,
  ERewardCalendarRewardCollection,
  OfferType
} from 'constants/enums';
import { v4 as uuidv4 } from 'uuid';
import { z } from 'zod';

export const REWARD_CALENDAR_TABS = [
  { value: ERewardCalendarFormState.GENERAL, label: 'Settings' },
  { value: ERewardCalendarFormState.CONFIGURATION, label: 'Configuration' },
  { value: ERewardCalendarFormState.OFFER_BEHAVIOR, label: 'Offer Behavior' }
];

export const REWARD_CALENDAR_DAYS_PER_WEEK = 7;
export const REWARD_CALENDAR_MIN_WEEKS = 1;
export const REWARD_CALENDAR_MAX_WEEKS = 10;

export const REWARD_CALENDAR_OFFERS_LIST_STALE_MS = 60_000;

export const MISSED_RESET_OPTIONS = [
  {
    value: ERewardCalendarMissedReset.COMPLETE,
    label: 'Complete Reset',
    description: '(Progress resets to Week 1, Day 1)'
  },
  {
    value: ERewardCalendarMissedReset.WEEKLY,
    label: 'Weekly Reset',
    description: '(Progress resets to Day 1 of the current week)'
  }
];

export const POPUP_TRIGGER_OPTIONS = [
  {
    value: ERewardCalendarPopUpTrigger.AUTOMATIC,
    label: 'Automatic',
    description: '(Opens when a reward becomes collectible)'
  },
  {
    value: ERewardCalendarPopUpTrigger.ON_CLICK,
    label: 'On Click',
    description: '(Opens only when players click the calendar widget)'
  }
];

export const REWARD_COLLECTION_OPTIONS = [
  {
    value: ERewardCalendarRewardCollection.FORCED,
    label: 'Forced Collection',
    description: '(Rewards cannot be skipped)'
  },
  {
    value: ERewardCalendarRewardCollection.MANUAL,
    label: 'Manual Collection',
    description: '(Rewards can be collected anytime during the week)'
  }
];

const REWARD_CALENDAR_DAY_SCHEMA = z.object({
  id: z.string(),
  products: z.array(z.any())
});

const REWARD_CALENDAR_WEEK_SCHEMA = z.object({
  id: z.string(),
  days: z
    .array(REWARD_CALENDAR_DAY_SCHEMA)
    .length(REWARD_CALENDAR_DAYS_PER_WEEK)
});

const dayHasFilledProducts = (products: unknown): boolean =>
  Array.isArray(products) &&
  products.some((row) => {
    if (!row || typeof row !== 'object') return false;
    const { productId, quantity } = row as Record<string, unknown>;
    const qty =
      typeof quantity === 'number' && !Number.isNaN(quantity)
        ? quantity
        : Number(quantity) || 0;
    return (
      typeof productId === 'string' && productId.trim().length > 0 && qty > 0
    );
  });

export const REWARD_CALENDAR_SCHEMA = z.object({
  _id: z.string().optional(),
  active: z.boolean(),
  name: z
    .string()
    .min(1, 'Name is required')
    .refine((value) => TRIMMED_INPUT_REGEX.test(value), {
      message: 'Name should not be spaces'
    }),
  publisherOfferId: z
    .string()
    .min(1, 'External ID is required')
    .refine((value) => TRIMMED_INPUT_REGEX.test(value), {
      message: 'External ID should not be spaces'
    }),
  subType: z.literal(EPopupSubType.REWARD_CALENDAR),
  offerUiId: z.string().min(1, 'Offer Design is required'),
  priority: z.number({ required_error: 'Priority is required' }).min(1),
  startOver: z.boolean(),
  missedRewardReset: z.nativeEnum(ERewardCalendarMissedReset),
  popUpTrigger: z.nativeEnum(ERewardCalendarPopUpTrigger),
  rewardCollection: z.nativeEnum(ERewardCalendarRewardCollection),
  segments: z.array(z.string()),
  type: z.literal(OfferType.POPUP),
  weeks: z
    .array(REWARD_CALENDAR_WEEK_SCHEMA)
    .min(REWARD_CALENDAR_MIN_WEEKS)
    .max(REWARD_CALENDAR_MAX_WEEKS)
}).superRefine((data, ctx) => {
  const everyDayFilled = data.weeks.every((week) =>
    week.days.every((day) => dayHasFilledProducts(day.products))
  );
  if (!everyDayFilled) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message:
        'Each calendar day must include at least one reward with quantity greater than zero.',
      path: ['weeks']
    });
  }
});

export type RewardCalendarFormFields = z.infer<typeof REWARD_CALENDAR_SCHEMA>;
export type RewardCalendarDay = z.infer<typeof REWARD_CALENDAR_DAY_SCHEMA>;
export type RewardCalendarWeek = z.infer<typeof REWARD_CALENDAR_WEEK_SCHEMA>;

export const buildEmptyDay = (): RewardCalendarDay => ({
  id: uuidv4(),
  products: []
});

export const buildEmptyWeek = (): RewardCalendarWeek => ({
  id: uuidv4(),
  days: Array.from({ length: REWARD_CALENDAR_DAYS_PER_WEEK }, buildEmptyDay)
});

export const REWARD_CALENDAR_DEFAULT_VALUES: RewardCalendarFormFields = {
  active: true,
  name: '',
  subType: EPopupSubType.REWARD_CALENDAR,
  publisherOfferId: '',
  offerUiId: '',
  priority: 1,
  startOver: true,
  missedRewardReset: ERewardCalendarMissedReset.COMPLETE,
  popUpTrigger: ERewardCalendarPopUpTrigger.ON_CLICK,
  rewardCollection: ERewardCalendarRewardCollection.FORCED,
  segments: [],
  type: OfferType.POPUP,
  weeks: [buildEmptyWeek()]
};
